import { parseToolbarQueryParam } from '../config/pacsConfig'
import {
  ENTITLEMENT_SOURCES,
  STORAGE_KEYS,
} from './constants'
import { parseThemeQueryParam } from './chromeTheme'
import { parsePlanQueryParam, shellShortFromLayout } from './parsePlanQuery'
import { notifyPlanChange } from './storage'

function canUseStorage() {
  try {
    return typeof window !== 'undefined' && !!window.localStorage
  } catch (e) {
    return false
  }
}

/**
 * @typedef {{
 *   plan: string,
 *   shell?: string|null,
 *   theme?: string|null,
 *   pacsId?: string|null,
 *   udvToken?: string|null,
 *   edgeId?: string|null,
 *   clinicName?: string|null,
 *   source: string,
 *   updatedAt?: number,
 * }} UdvEntitlements
 */

/**
 * @returns {UdvEntitlements|null}
 */
export function loadEntitlements() {
  if (!canUseStorage()) return null
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.ENTITLEMENTS)
    if (raw == null || raw === '') return null
    const v = JSON.parse(raw)
    if (!v || typeof v !== 'object') return null
    const plan = parsePlanQueryParam(v.plan)
    if (!plan) return null
    return {
      plan,
      shell: v.shell != null ? String(v.shell) : null,
      theme: parseThemeQueryParam(v.theme),
      pacsId: v.pacsId != null && String(v.pacsId).trim() !== ''
        ? String(v.pacsId).trim()
        : null,
      udvToken:
        v.udvToken != null && String(v.udvToken).trim() !== ''
          ? String(v.udvToken).trim()
          : null,
      edgeId:
        v.edgeId != null && String(v.edgeId).trim() !== ''
          ? String(v.edgeId).trim()
          : null,
      clinicName:
        v.clinicName != null && String(v.clinicName).trim() !== ''
          ? String(v.clinicName).trim()
          : null,
      source: v.source || ENTITLEMENT_SOURCES.CONTROLLER,
      updatedAt: typeof v.updatedAt === 'number' ? v.updatedAt : undefined,
    }
  } catch (e) {
    return null
  }
}

/**
 * Persiste entitlements y notifica cambio de plan.
 * @param {Partial<UdvEntitlements> & { plan: string }} data
 * @returns {UdvEntitlements|null}
 */
export function saveEntitlements(data) {
  if (!data || typeof data !== 'object') return null
  const plan = parsePlanQueryParam(data.plan)
  if (!plan) return null

  let shell = data.shell != null ? String(data.shell) : null
  if (shell) {
    const layout = parseToolbarQueryParam(shell)
    shell = shellShortFromLayout(layout) || String(shell).toLowerCase()
  }
  const theme = parseThemeQueryParam(data.theme)

  /** @type {UdvEntitlements} */
  const next = {
    plan,
    shell: shell || null,
    theme: theme || null,
    pacsId:
      data.pacsId != null && String(data.pacsId).trim() !== ''
        ? String(data.pacsId).trim()
        : null,
    udvToken:
      data.udvToken != null && String(data.udvToken).trim() !== ''
        ? String(data.udvToken).trim()
        : null,
    edgeId:
      data.edgeId != null && String(data.edgeId).trim() !== ''
        ? String(data.edgeId).trim()
        : null,
    clinicName:
      data.clinicName != null && String(data.clinicName).trim() !== ''
        ? String(data.clinicName).trim()
        : null,
    source: data.source || ENTITLEMENT_SOURCES.CONTROLLER,
    updatedAt: Date.now(),
  }

  if (!canUseStorage()) return next
  try {
    window.localStorage.setItem(STORAGE_KEYS.ENTITLEMENTS, JSON.stringify(next))
  } catch (e) {
    /* ignore quota */
  }
  notifyPlanChange()
  return next
}

export function clearEntitlements() {
  if (!canUseStorage()) return
  try {
    window.localStorage.removeItem(STORAGE_KEYS.ENTITLEMENTS)
  } catch (e) {
    /* ignore */
  }
  notifyPlanChange()
}

/**
 * Snapshot de links[] del Controller (multiclínica).
 * @returns {object[]}
 */
export function loadControllerLinks() {
  if (!canUseStorage()) return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEYS.CONTROLLER_LINKS)
    if (!raw) return []
    const v = JSON.parse(raw)
    return Array.isArray(v) ? v : []
  } catch (e) {
    return []
  }
}

/**
 * @param {object[]|null|undefined} links
 */
export function saveControllerLinks(links) {
  if (!canUseStorage()) return
  try {
    const list = Array.isArray(links) ? links : []
    window.localStorage.setItem(
      STORAGE_KEYS.CONTROLLER_LINKS,
      JSON.stringify(list)
    )
  } catch (e) {
    /* ignore */
  }
}

/**
 * Entitlements clínicos del vínculo usuario↔edge para el origen del link.
 * @param {string|null|undefined} originEdgeId
 * @param {object[]|null|undefined} [controllerLinks]
 * @returns {UdvEntitlements|null}
 */
export function resolveOriginLinkEntitlements(originEdgeId, controllerLinks) {
  const want =
    originEdgeId != null && String(originEdgeId).trim() !== ''
      ? String(originEdgeId).trim()
      : null
  if (!want) return null
  const links =
    controllerLinks !== undefined ? controllerLinks : loadControllerLinks()
  const link = pickControllerLink(links, want, { exactOnly: true })
  if (!link) return null
  const draft = entitlementsFromControllerLink(link)
  if (!draft) return null
  return {
    ...draft,
    source: ENTITLEMENT_SOURCES.ORIGIN_LINK,
  }
}

/**
 * Elige link activo: preferredEdgeId, o el primero con plan, o el primero.
 * @param {object[]|null|undefined} links
 * @param {string|null|undefined} preferredEdgeId
 * @returns {object|null}
 */
export function pickControllerLink(links, preferredEdgeId, opts = {}) {
  const exactOnly = opts.exactOnly === true
  if (!Array.isArray(links) || links.length === 0) return null
  const want =
    preferredEdgeId != null && String(preferredEdgeId).trim() !== ''
      ? String(preferredEdgeId).trim()
      : null
  if (want) {
    for (let i = 0; i < links.length; i++) {
      const L = links[i]
      if (L && String(L.edge_id || L.edgeId || '') === want) return L
    }
    if (exactOnly) return null
  }
  for (let i = 0; i < links.length; i++) {
    const L = links[i]
    if (L && parsePlanQueryParam(L.plan)) return L
  }
  return links[0] || null
}

/**
 * @param {object|null|undefined} link
 * @returns {UdvEntitlements|null} null si el link no trae plan válido
 */
export function entitlementsFromControllerLink(link) {
  if (!link || typeof link !== 'object') return null
  const plan = parsePlanQueryParam(link.plan)
  if (!plan) return null
  const shellRaw = link.shell || link.default_shell || link.defaultShell || null
  const themeRaw = link.theme || link.chrome_theme || link.chromeTheme || null
  const pacsId = link.pacs_id || link.pacsId || null
  const udvToken = link.udv_token || link.udvToken || null
  const edgeId = link.edge_id || link.edgeId || null
  const clinicName = link.clinic_name || link.clinicName || null
  return {
    plan,
    shell: shellRaw,
    theme: parseThemeQueryParam(themeRaw),
    pacsId,
    udvToken,
    edgeId,
    clinicName,
    source: ENTITLEMENT_SOURCES.CONTROLLER,
  }
}

/**
 * Aplica links[] del Controller: pick + save si hay plan.
 * @param {object[]|null|undefined} links
 * @param {{ preferredEdgeId?: string|null, silent?: boolean }} [opts]
 * @returns {{ entitlements: UdvEntitlements|null, link: object|null, missingPlan: boolean }}
 */
export function applyControllerLinks(links, opts = {}) {
  const link = pickControllerLink(links, opts.preferredEdgeId)
  if (!link) {
    return { entitlements: null, link: null, missingPlan: false }
  }
  // Pass-through: edge_lan_base / edge_wado_url from /auth/me stay on the link object.
  const draft = entitlementsFromControllerLink(link)
  if (!draft) {
    return { entitlements: null, link, missingPlan: true }
  }
  if (!opts.silent) {
    const saved = saveEntitlements(draft)
    saveControllerLinks(links)
    return { entitlements: saved, link, missingPlan: false }
  }
  return { entitlements: draft, link, missingPlan: false }
}

/**
 * Persistir desde deep-link URL válido.
 * @param {{
 *   plan: string,
 *   shell?: string|null,
 *   theme?: string|null,
 *   pacsId?: string|null,
 *   udvToken?: string|null,
 * }} data
 * @returns {UdvEntitlements|null}
 */
export function persistEntitlementsFromUrl(data) {
  if (!data || !data.plan) return null
  return saveEntitlements({
    plan: data.plan,
    shell: data.shell || null,
    theme: data.theme || null,
    pacsId: data.pacsId || null,
    udvToken: data.udvToken || null,
    edgeId: null,
    clinicName: null,
    source: ENTITLEMENT_SOURCES.URL,
  })
}
